/**
 * @poc-to-production
 *
 * Browser session for APS PKCE login. Persists to localStorage so sign-in
 * survives browser restarts; refresh_token renews access without re-login.
 */
import { create } from "zustand";
import {
  getApsClientId,
  getApsScopes,
  getAuthorizeUrl,
  getRedirectUri,
  getTokenUrl,
  isAuthDisabled,
} from "../auth/apsConfig";
import {
  buildAuthorizationUrl,
  exchangeCodeForToken,
  refreshAccessToken,
  takePkceVerifier,
} from "../auth/pkce";
import {
  fetchUserProfile,
  isDefaultApsProfilePicture,
  type VeraUserProfile,
} from "../auth/userProfile";
import { hasAlphaAccess } from "../featureFlags/alphaAccess";
import { ALPHA_SUPPORT_MESSAGE } from "../featureFlags/launchDarklyConfig";

const SESSION_KEY = "vera_aps_session";
/** Legacy key — migrated to localStorage on hydrate. */
const LEGACY_SESSION_KEY = "vera_aps_session";
const SILENT_ATTEMPT_KEY = "vera_silent_auth_attempted";
/** Persists across restarts so explicit sign-out blocks silent SSO. */
const MANUAL_SIGNOUT_KEY = "vera_manual_signout";

/** Refresh if access token expires within this window. */
const EXPIRY_BUFFER_MS = 60_000;

function persistGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persistSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / quota — session still works in memory for this tab.
  }
}

function persistRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function clearManualSignOut(): void {
  persistRemove(MANUAL_SIGNOUT_KEY);
}

export function markManualSignOut(): void {
  persistSet(MANUAL_SIGNOUT_KEY, "1");
}

export function hasManualSignOut(): boolean {
  return persistGet(MANUAL_SIGNOUT_KEY) === "1";
}

export function markSilentAuthAttempted(): void {
  sessionStorage.setItem(SILENT_ATTEMPT_KEY, "1");
}

export function hasSilentAuthAttempted(): boolean {
  return sessionStorage.getItem(SILENT_ATTEMPT_KEY) === "1";
}

interface StoredSession {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
  profile: VeraUserProfile;
  /** @deprecated legacy sessions — migrated on hydrate */
  userName?: string;
}

interface AuthState {
  accessToken: string | null;
  profile: VeraUserProfile | null;
  expiresAt: number | null;
  status:
    | "idle"
    | "redirecting"
    | "loading"
    | "authenticated"
    | "error"
    | "access_denied";
  error: string | null;
  hydrate: () => void;
  login: () => Promise<void>;
  trySilentLogin: () => Promise<void>;
  handleOAuthCallback: (code: string, state: string | null) => Promise<void>;
  logout: () => void;
  getAccessToken: () => string | null;
  /** Returns a valid access token, refreshing with refresh_token when needed. */
  ensureAccessToken: () => Promise<string | null>;
}

function isAccessTokenValid(expiresAt: number): boolean {
  return Date.now() < expiresAt - EXPIRY_BUFFER_MS;
}

function migrateLegacySession(): void {
  try {
    const legacy = sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (!legacy || persistGet(SESSION_KEY)) return;
    persistSet(SESSION_KEY, legacy);
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // ignore
  }
}

function readStoredSession(): StoredSession | null {
  migrateLegacySession();
  try {
    const raw = persistGet(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.accessToken || !parsed.expiresAt) return null;
    if (!parsed.profile && parsed.userName) {
      parsed.profile = { displayName: parsed.userName };
    }
    if (!parsed.profile?.displayName) return null;
    if (isDefaultApsProfilePicture(parsed.profile.picture)) {
      parsed.profile.picture = null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  persistSet(SESSION_KEY, JSON.stringify(session));
}

function clearStoredSession(): void {
  persistRemove(SESSION_KEY);
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // ignore
  }
}

function isSparseProfile(profile: VeraUserProfile): boolean {
  return profile.displayName === "Autodesk user" || !profile.email;
}

function shouldRefreshProfile(profile: VeraUserProfile): boolean {
  return isSparseProfile(profile) || profile.picture == null;
}

async function refreshStoredProfileIfNeeded(): Promise<void> {
  const stored = readStoredSession();
  if (!stored || !shouldRefreshProfile(stored.profile)) return;
  const refreshed = await fetchUserProfile(stored.accessToken, stored.idToken);
  const merged = { ...stored.profile, ...refreshed };
  if (!isSparseProfile(merged) || merged.picture !== stored.profile.picture) {
    writeStoredSession({ ...stored, profile: merged });
    useAuthStore.setState({ profile: merged });
  }
}

function applySessionToStore(session: StoredSession): void {
  useAuthStore.setState({
    accessToken: session.accessToken,
    profile: session.profile,
    expiresAt: session.expiresAt,
    status: "authenticated",
    error: null,
  });
}

function denyAlphaAccess(profile?: VeraUserProfile | null): void {
  clearStoredSession();
  markManualSignOut();
  markSilentAuthAttempted();
  useAuthStore.setState({
    accessToken: null,
    profile: profile?.email
      ? {
          displayName: profile.displayName,
          email: profile.email,
        }
      : null,
    expiresAt: null,
    status: "access_denied",
    error: ALPHA_SUPPORT_MESSAGE,
  });
}

async function ensureAlphaAccess(profile: VeraUserProfile): Promise<boolean> {
  const allowed = await hasAlphaAccess(profile.email);
  if (allowed) return true;
  denyAlphaAccess(profile);
  return false;
}

function sessionFromTokenResponse(
  token: Awaited<ReturnType<typeof exchangeCodeForToken>>,
  profile: VeraUserProfile
): StoredSession {
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    idToken: token.id_token,
    expiresAt: Date.now() + token.expires_in * 1000 - EXPIRY_BUFFER_MS,
    profile,
  };
}

let refreshInFlight: Promise<string | null> | null = null;

async function performTokenRefresh(
  stored: StoredSession
): Promise<string | null> {
  if (!stored.refreshToken) return null;
  try {
    const token = await refreshAccessToken({
      tokenUrl: getTokenUrl(),
      clientId: getApsClientId(),
      refreshToken: stored.refreshToken,
    });
    const profile =
      stored.profile ??
      (await fetchUserProfile(token.access_token, token.id_token));
    const next = sessionFromTokenResponse(token, profile);
    // APS may omit a new refresh_token — keep the previous one.
    if (!token.refresh_token && stored.refreshToken) {
      next.refreshToken = stored.refreshToken;
    }
    writeStoredSession(next);
    if (!(await ensureAlphaAccess(next.profile))) {
      return null;
    }
    applySessionToStore(next);
    return next.accessToken;
  } catch {
    clearStoredSession();
    useAuthStore.setState({
      accessToken: null,
      profile: null,
      expiresAt: null,
      status: "idle",
      error: null,
    });
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  profile: null,
  expiresAt: null,
  status: "idle",
  error: null,

  hydrate: () => {
    const stored = readStoredSession();
    if (!stored) {
      set({
        status: "idle",
        accessToken: null,
        profile: null,
        expiresAt: null,
      });
      return;
    }

    if (isAccessTokenValid(stored.expiresAt)) {
      set({ status: "loading", error: null });
      void ensureAlphaAccess(stored.profile).then(allowed => {
        if (!allowed) return;
        applySessionToStore(stored);
        void refreshStoredProfileIfNeeded();
      });
      return;
    }

    if (stored.refreshToken) {
      set({ status: "loading", error: null });
      void get()
        .ensureAccessToken()
        .then(token => {
          if (!token) set({ status: "idle" });
        });
      return;
    }

    clearStoredSession();
    set({
      status: "idle",
      accessToken: null,
      profile: null,
      expiresAt: null,
    });
  },

  ensureAccessToken: async () => {
    const stored = readStoredSession();
    if (!stored) {
      set({
        status: "idle",
        accessToken: null,
        profile: null,
        expiresAt: null,
      });
      return null;
    }

    if (isAccessTokenValid(stored.expiresAt)) {
      applySessionToStore(stored);
      return stored.accessToken;
    }

    if (!stored.refreshToken) {
      clearStoredSession();
      set({
        status: "idle",
        accessToken: null,
        profile: null,
        expiresAt: null,
      });
      return null;
    }

    if (!refreshInFlight) {
      refreshInFlight = performTokenRefresh(stored).finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  },

  login: async () => {
    clearManualSignOut();
    markSilentAuthAttempted();
    set({ status: "redirecting", error: null });
    try {
      const url = await buildAuthorizationUrl({
        authorizeUrl: getAuthorizeUrl(),
        clientId: getApsClientId(),
        redirectUri: getRedirectUri(),
        scope: getApsScopes(),
        prompt: "login",
      });
      window.location.assign(url);
    } catch (err) {
      set({
        status: "error",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },

  trySilentLogin: async () => {
    if (hasManualSignOut() || hasSilentAuthAttempted()) return;
    markSilentAuthAttempted();
    set({ status: "redirecting", error: null });
    try {
      const url = await buildAuthorizationUrl({
        authorizeUrl: getAuthorizeUrl(),
        clientId: getApsClientId(),
        redirectUri: getRedirectUri(),
        scope: getApsScopes(),
        prompt: "none",
      });
      window.location.assign(url);
    } catch {
      set({
        status: "idle",
        error: null,
      });
    }
  },

  handleOAuthCallback: async (code, state) => {
    set({ status: "loading", error: null });
    try {
      const verifier = takePkceVerifier(state);
      const token = await exchangeCodeForToken({
        tokenUrl: getTokenUrl(),
        clientId: getApsClientId(),
        redirectUri: getRedirectUri(),
        code,
        codeVerifier: verifier,
      });
      const profile = await fetchUserProfile(
        token.access_token,
        token.id_token
      );
      if (!(await ensureAlphaAccess(profile))) return;
      const session = sessionFromTokenResponse(token, profile);
      clearManualSignOut();
      writeStoredSession(session);
      applySessionToStore(session);
    } catch (err) {
      set({
        status: "error",
        error: err instanceof Error ? err.message : String(err),
        accessToken: null,
        profile: null,
        expiresAt: null,
      });
      throw err;
    }
  },

  logout: () => {
    clearStoredSession();
    markManualSignOut();
    markSilentAuthAttempted();
    set({
      accessToken: null,
      profile: null,
      expiresAt: null,
      status: "idle",
      error: null,
    });
  },

  getAccessToken: () => {
    const { accessToken, expiresAt } = get();
    if (!accessToken || !expiresAt || !isAccessTokenValid(expiresAt)) {
      return null;
    }
    return accessToken;
  },
}));

/** Local dev bypass — call once before render when isAuthDisabled(). */
export function initDevAuthBypass(): void {
  if (!isAuthDisabled()) return;
  useAuthStore.setState({
    accessToken: "dev-bypass",
    profile: { displayName: "Local Dev" },
    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
    status: "authenticated",
    error: null,
  });
}

export type { VeraUserProfile };
